"use client";

// Sanity.io imports
import { PortableText } from "next-sanity";

// Component & Template imports
import NextLink from "@/components/interactive/NextLink";
import HexPattern from "@/components/layout/HexPattern";
import FormRenderer from "@/components/interactive/FormRenderer";
import NextImage from "@/components/interactive/NextImage";

// Hooks, Utility & Helper function imports
import { createPortableTextComponents } from "@/utils/portableTextComponents";
import { t } from "@/utils/t";

import { IsovBodyCta, IsovButton } from "@/components/stencil";

interface BodyCTAProps {
  theme?: string;
  icon?: string;
  logo?: string;
  logoWhite?: string;
  eyebrow?: any;
  headline?: any;
  subtext?: any;
  description?: any;
  text?: any;
  formId?: string;
  minHeight?: number;
  webexMeetingId?: string;
  redirectUrl?: string;
  thankYouMessage?: any;
  ctas?: any;
  alignment?: string;
  locale?: string;
  decorated?: boolean;
  shaded?: boolean;
  showPattern?: boolean;
  backgroundImage?: any;
  skeleton?: boolean;
}

export default function BodyCTA({
  theme,
  icon,
  logo,
  logoWhite,
  eyebrow,
  headline,
  subtext,
  description,
  text,
  formId,
  minHeight,
  webexMeetingId,
  redirectUrl,
  thankYouMessage,
  ctas,
  alignment,
  decorated,
  shaded,
  showPattern,
  backgroundImage,
  locale = "en",
  skeleton,
}: BodyCTAProps) {
  const ptComponents = createPortableTextComponents(locale);
  const logoImage: any = theme === "dark" && logoWhite ? logoWhite : logo;

  return (
    <IsovBodyCta
      theme={theme}
      eyebrow={t(eyebrow, locale)}
      headline={t(headline, locale)}
      subtext={t(subtext, locale)}
      alignment={alignment}
      decorated={decorated}
      shaded={shaded}
      className="pb-8"
      skeleton={skeleton}
    >
      {showPattern && (
        <HexPattern theme={theme} variant="bodyCta" className="-mt-10" />
      )}

      {backgroundImage && backgroundImage?.image && (
        <NextImage
          slot="background"
          src={backgroundImage.image}
          alt={backgroundImage.image || "Image"}
          className="h-full w-full object-cover object-right-top"
          maxWidth={1080}
          width={1080}
          height={1080}
          sizes="100vw"
          isOptimized
          priority
        />
      )}

      {logoImage && (
        <div
          slot="logo"
          className="w-auto h-10 mb-2 mx-auto sm:mx-0 overflow-hidden object-contain"
        >
          <NextImage
            src={logoImage}
            alt={t(headline, locale) || "Image"}
            className="h-full w-full object-contain"
            maxWidth={300}
            width={300}
            height={110}
            sizes="(max-width: 640px) 50vw, 300px"
            isOptimized
            priority
          />
        </div>
      )}

      {text && (
        <>
          {typeof text === "string" ? (
            text
          ) : (
            <PortableText
              value={t(text, locale)}
              components={ptComponents}
              onMissingComponent={(message, options) => {
                console.warn(message, {
                  type: options.type,
                  nodeType: options.nodeType,
                });
              }}
            />
          )}
        </>
      )}

      {description && (
        <>
          {typeof description === "string" ? (
            description
          ) : (
            <PortableText
              value={t(description, locale)}
              components={ptComponents}
              onMissingComponent={(message, options) => {
                console.warn(message, {
                  type: options.type,
                  nodeType: options.nodeType,
                });
              }}
            />
          )}
        </>
      )}

      {formId && (
        <FormRenderer
          theme={theme}
          formId={formId}
          minHeight={minHeight}
          webexMeetingId={webexMeetingId}
          redirectUrl={redirectUrl}
          thankYouMessage={thankYouMessage}
          locale={locale}
        />
      )}

      {ctas?.map((cta: any) => (
        <NextLink
          key={`cta-${cta?._key}-${t(cta?.label, locale)}`}
          href={cta?.url}
          target={cta?.openInNewTab ? "_blank" : undefined}
          type={`button_${cta?.variant ?? "default"}`}
          slot="cta"
        >
          <IsovButton
            theme={theme}
            variant={cta?.variant}
            label={t(cta?.label, locale)}
            skeleton={skeleton}
          />
        </NextLink>
      ))}
    </IsovBodyCta>
  );
}
